import { queries } from '@testing-library/react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useGetAttachedFile } from '../../../hooks/queries/attachedFile';
import { usePostDailyMatterReportSearch } from '../../../hooks/queries/dailyMatterReport';
import useToastHook from '../../../hooks/ToastHook';
import { EngeneFileObject } from '../../../models/EngeneFileObject';
import { DailyMatterReportGetResultData } from '../../../models/gen/dailyMatterReportGetResultData';
import { FileKind } from '../../../models/gen/fileKind';
import NavigateGoBack from '../../../routes/GoBack';
import Wireframe from '../../wireframe/Wireframe';
import { ReportState } from './Report';

/**
 * 作業報告書画面パラメータ
 */
export type WorkReportParams = {
  /**
   * 日毎現場ID
   */
  dailyMatterId: string;
};

export type WorkReportOutletContext =
  | DailyMatterReportGetResultData
  | undefined;

/**
 * 作業報告書画面
 * @returns
 */
const WorkReport: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const params = useParams<WorkReportParams>();
  if (
    TypeUtils.isNil(params.dailyMatterId) ||
    Number.isNaN(parseInt(params.dailyMatterId))
  ) {
    return <NavigateGoBack />;
  }

  const form = useForm<ReportState>();

  const { data, error, isError, isLoading, refetch } =
    usePostDailyMatterReportSearch({
      id: parseInt(params.dailyMatterId),
    });
  const getAttachedFile = useGetAttachedFile();
  React.useState(() => {
    if (!TypeUtils.isNilString(params.dailyMatterId)) {
      refetch();
    }
  });
  React.useEffect(() => {
    if (!isLoading) {
      const attachFiles = data?.result?.dailyMatterReport?.attachFiles;
      if (Array.isArray(attachFiles) && attachFiles.length > 0) {
        const queries = attachFiles
          .filter(
            (attachFile) =>
              TypeUtils.isNonNilNumber(attachFile.id) &&
              TypeUtils.isNonNilString(attachFile.fileName)
          )
          .map((attachFile) => ({
            mode: FileKind.ReportDoc,
            id: attachFile.id!,
            filename: attachFile.fileName!,
          }));
        getAttachedFile.mutate(queries, {
          onSuccess: (results) => {
            if (TypeUtils.isNonNilArray(results)) {
              form.reset({
                dailyMatterId: data?.result?.dailyMatterReport?.dailyMatterId,
                dailyMatterVersion:
                  data?.result?.dailyMatterReport?.dailyMatterVersion,
                reportDocMemo: data?.result?.dailyMatterReport?.reportDocMemo,
                reports: results.map((result, idx) =>
                  EngeneFileObject.fromDownloaded(
                    result.file,
                    queries[idx].id,
                    queries[idx].filename || result.filename,
                    FileKind.ReportDoc
                  )
                ),
              });
            }
          },
        });
      } else {
        form.reset({
          dailyMatterId: data?.result?.dailyMatterReport?.dailyMatterId,
          dailyMatterVersion:
            data?.result?.dailyMatterReport?.dailyMatterVersion,
          reportDocMemo: data?.result?.dailyMatterReport?.reportDocMemo,
        });
      }
    }
  }, [isLoading, data?.result]);
  const { toast } = useToastHook();
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError ? error : '作業報告書の取得に失敗しました'
      );
    }
  }, [isError]);

  return (
    <Wireframe isLoaded={!isLoading} title={pageNames.WORK_REPORT}>
      <FormProvider {...form}>
        <Outlet context={{ ...data?.result }} />
      </FormProvider>
    </Wireframe>
  );
};

export default WorkReport;
